import { useCallback } from 'react'

import { useIsMobile } from '@/hooks/use-mobile.hook'
import type { ResizableDrawerProps } from './resizable-drawer.interface'
import { ResizableDrawerDesktop } from './resizable-drawer-desktop.atom'
import { ResizableDrawerMobile } from './resizable-drawer-mobile.atom'
import { ResizableDrawerOverlay } from './resizable-drawer-overlay.atom'
import { useResizableDrawerWidth } from './use-resizable-drawer-width.hook'

/**
 * Unified resizable drawer organism that handles desktop/mobile detection,
 * overlay, resize, and width state.
 * On desktop: renders an overlay + resizable panel with slide-in animation.
 * On mobile: renders a shadcn Sheet.
 */
export const ResizableDrawer: React.FC<ResizableDrawerProps> = ({
  isOpen,
  onOpenChange,
  title,
  description,
  children,
  className,
}) => {
  const isMobile = useIsMobile()
  const { drawerWidth, handleResize } = useResizableDrawerWidth()

  const handleClose = useCallback(() => {
    onOpenChange(false)
  }, [onOpenChange])

  if (isMobile) {
    return (
      <ResizableDrawerMobile
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        title={title}
        description={description}
        className={className}
      >
        {children}
      </ResizableDrawerMobile>
    )
  }

  return (
    <>
      <ResizableDrawerOverlay
        isVisible={isOpen}
        onClick={handleClose}
        className={className ? `${className}-overlay` : undefined}
      />
      <ResizableDrawerDesktop
        isOpen={isOpen}
        drawerWidth={drawerWidth}
        onResize={handleResize}
        className={className}
      >
        {children}
      </ResizableDrawerDesktop>
    </>
  )
}

ResizableDrawer.displayName = 'ResizableDrawer'
